import { forwardRef, useEffect, useState, type ChangeEvent, type InputHTMLAttributes } from 'react';
import './Toggle.css';

export type ToggleSize = 'medium' | 'small';

export type ToggleVisualState = 'default' | 'pressed';

/** Where the label sits relative to the switch: after it (Figma default) or before it. */
export type ToggleLabelPosition = 'start' | 'end';

/** On/off switch toggle with optional label. Renders as a styled checkbox with `role="switch"`. */
export type ToggleProps = {
  label?: string;
  /** `end` (default) renders the label after the switch; `start` renders it before (e.g. filter bars). */
  labelPosition?: ToggleLabelPosition;
  /** Current checked state. When provided, the component is controlled. */
  active?: boolean;
  size?: ToggleSize;
  disabled?: boolean;
  /**
   * Storybook-only visual state override to display pressed static examples.
   * Keep undefined in real app usage.
   */
  visualState?: ToggleVisualState;
  showFocus?: boolean;
} & Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'checked' | 'disabled' | 'size'>;

export const Toggle = forwardRef<HTMLInputElement, ToggleProps>(function Toggle({
  label,
  labelPosition = 'end',
  active = true,
  size = 'medium',
  disabled = false,
  visualState,
  showFocus = false,
  className = '',
  id,
  ...rest
}, ref) {
  const [isActive, setIsActive] = useState(active);

  useEffect(() => {
    setIsActive(active);
  }, [active]);

  const inputId = id ?? `spark-toggle-${Math.random().toString(36).slice(2, 9)}`;
  const hasLabel = Boolean(label?.length);
  const onChangeFromProps = rest.onChange;

  const rootClasses = [
    'spark-toggle',
    `spark-toggle-${size}`,
    `spark-toggle-label-${labelPosition}`,
    isActive ? 'spark-toggle-active' : 'spark-toggle-inactive',
    disabled ? 'spark-toggle-disabled' : '',
    visualState ? `spark-toggle-state-${visualState}` : '',
    showFocus ? 'spark-toggle-show-focus' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (!disabled) {
      setIsActive(event.target.checked);
    }
    onChangeFromProps?.(event);
  };

  const labelNode = hasLabel ? <span className="spark-toggle-label">{label}</span> : null;

  return (
    <label className={rootClasses} htmlFor={inputId}>
      <input
        ref={ref}
        id={inputId}
        className="spark-toggle-input"
        type="checkbox"
        role="switch"
        checked={isActive}
        disabled={disabled}
        aria-checked={isActive}
        onChange={handleChange}
        {...rest}
      />
      {labelPosition === 'start' ? labelNode : null}
      <span className="spark-toggle-track" aria-hidden="true">
        <span className="spark-toggle-handle" />
      </span>
      {labelPosition === 'end' ? labelNode : null}
      <span className="spark-toggle-focus" aria-hidden="true" />
    </label>
  );
});

Toggle.displayName = 'Toggle';
